import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Phone, TriangleAlert } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { Spinner } from "@/components/kit";
import { trackVet } from "@/components/VetCard";

const FIRST_AID = [
  { title: "Keep calm & stay safe", desc: "Speak softly. A frightened dog may bite. Approach slowly." },
  { title: "Check breathing & responsiveness", desc: "Watch the chest rise. Try to call their name softly." },
  { title: "Do NOT give food, water or medications", desc: "Unless specifically told to by a licensed vet on the phone." },
  { title: "Call a vet immediately", desc: "Time is critical — call one of the 24/7 clinics below now." },
];

export default function Emergency() {
  const navigate = useNavigate();
  const [vets, setVets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch("/vets?emergency=true", {}, false)
      .then((v) => setVets(v || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const primary = vets[0];

  return (
    <div className="-mx-4 sm:-mx-6 -my-6 min-h-[calc(100vh-4rem)] bg-[#D9534F] text-white">
      <div className="max-w-2xl mx-auto px-5 py-6">
        <div className="flex items-center justify-between mb-2">
          <button data-testid="emergency-back-btn" onClick={() => navigate(-1)} className="h-10 w-10 rounded-full bg-white/15 flex items-center justify-center">
            <ChevronLeft size={22} color="#fff" />
          </button>
          <h1 className="font-display text-xl font-extrabold">Emergency</h1>
          <div className="w-10" />
        </div>

        <div className="rounded-3xl bg-white/15 p-5 text-center mb-4">
          <div className="flex justify-center mb-2"><TriangleAlert size={30} /></div>
          <p className="font-display text-2xl font-black">Serious symptoms?</p>
          <p className="text-white/90 mt-1">Call a 24/7 emergency vet immediately. Every minute matters.</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-10"><Spinner className="!border-white/30 !border-t-white" /></div>
        ) : (
          <>
            {primary && (
              <a
                href={`tel:${primary.phone}`}
                data-testid="emergency-call-primary"
                onClick={() => trackVet(primary.id, "call")}
                className="flex items-center gap-4 rounded-3xl bg-white p-4 mb-6"
              >
                <div className="h-14 w-14 rounded-full bg-[#FDECEB] flex items-center justify-center shrink-0">
                  <Phone size={26} color="#D9534F" />
                </div>
                <div className="min-w-0">
                  <p className="text-[#D9534F] text-xs font-extrabold uppercase tracking-wide">Call now</p>
                  <p className="text-[#2D2B2A] font-extrabold truncate">{primary.name}</p>
                  <p className="text-[#4A4746] text-sm">{primary.phone}</p>
                </div>
              </a>
            )}

            <p className="font-display text-lg font-extrabold mb-3">First aid steps</p>
            <div className="space-y-3 mb-6">
              {FIRST_AID.map((s, i) => (
                <div key={i} className="flex gap-3 rounded-2xl bg-white/12 p-3">
                  <div className="h-8 w-8 rounded-full bg-white text-[#D9534F] font-extrabold flex items-center justify-center shrink-0">{i + 1}</div>
                  <div>
                    <p className="font-extrabold">{s.title}</p>
                    <p className="text-white/90 text-sm">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {vets.length > 1 && (
              <>
                <p className="font-display text-lg font-extrabold mb-3">Other 24/7 clinics</p>
                <div className="space-y-2">
                  {vets.slice(1).map((v) => (
                    <a
                      key={v.id}
                      href={`tel:${v.phone}`}
                      data-testid={`emergency-call-${v.id}`}
                      onClick={() => trackVet(v.id, "call")}
                      className="flex items-center justify-between rounded-2xl bg-white p-4"
                    >
                      <div className="min-w-0">
                        <p className="text-[#2D2B2A] font-bold truncate">{v.name}</p>
                        <p className="text-[#4A4746] text-sm">{v.phone} · {v.distance_km?.toFixed?.(1)} km</p>
                      </div>
                      <Phone size={20} color="#D9534F" />
                    </a>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
